import { makeStyles } from '@material-ui/core/styles'
import MicIcon from '@material-ui/icons/Mic'
import React from 'react'
import { useTranslate } from 'react-admin'

interface VoiceCloseConfirmBannerProps {
  onKeepListening: () => void
  onEndSession: () => void
  compact?: boolean
}

const useStyles = makeStyles(() => ({
  voiceCloseConfirmBanner: {
    background: 'linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%)',
    border: '1px solid #1976d2',
    borderRadius: 12,
    padding: '16px',
    margin: '12px 12px 0',
    boxShadow: '0 2px 8px rgba(23, 81, 208, 0.15)',
    animation: '$slideInFromTop 0.3s ease-out',
  },
  voiceCloseConfirmBannerCompact: {
    background: 'linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%)',
    border: '1px solid #1976d2',
    borderRadius: 8,
    padding: '12px',
    margin: '8px 8px 0',
    boxShadow: '0 1px 4px rgba(23, 81, 208, 0.15)',
    animation: '$slideInFromTop 0.3s ease-out',
  },
  voiceCloseConfirmContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  voiceCloseConfirmContentCompact: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  voiceCloseConfirmHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 4,
  },
  voiceCloseConfirmHeaderCompact: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  voiceCloseConfirmIcon: {
    fontSize: '20px',
    color: '#1751D0',
  },
  voiceCloseConfirmTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1751D0',
    margin: 0,
  },
  voiceCloseConfirmTitleCompact: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1751D0',
    margin: 0,
  },
  voiceCloseConfirmText: {
    fontSize: '13px',
    color: '#1976d2',
    lineHeight: 1.4,
    margin: 0,
  },
  voiceCloseConfirmTextCompact: {
    fontSize: '12px',
    color: '#1976d2',
    lineHeight: 1.3,
    margin: 0,
  },
  voiceCloseConfirmActions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  voiceCloseConfirmActionsCompact: {
    display: 'flex',
    gap: 6,
    justifyContent: 'flex-end',
    marginTop: 6,
  },
  voiceCloseConfirmButton: {
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: '12px',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, transform 0.1s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  voiceCloseConfirmButtonCompact: {
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: '11px',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, transform 0.1s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  voiceCloseConfirmButtonSecondary: {
    backgroundColor: 'rgba(23, 81, 208, 0.1)',
    color: '#1751D0',
    '&:hover': {
      backgroundColor: 'rgba(23, 81, 208, 0.2)',
    },
  },
  voiceCloseConfirmButtonPrimary: {
    backgroundColor: '#1751D0',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  },
  '@keyframes slideInFromTop': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}))

const VoiceCloseConfirmBanner: React.FC<VoiceCloseConfirmBannerProps> = ({
  onKeepListening,
  onEndSession,
  compact = false,
}) => {
  const classes = useStyles()
  const translate = useTranslate()

  return (
    <div style={{ background: 'white' }}>
      <div className={compact ? classes.voiceCloseConfirmBannerCompact : classes.voiceCloseConfirmBanner}>
        <div className={compact ? classes.voiceCloseConfirmContentCompact : classes.voiceCloseConfirmContent}>
          <div className={compact ? classes.voiceCloseConfirmHeaderCompact : classes.voiceCloseConfirmHeader}>
            <MicIcon className={classes.voiceCloseConfirmIcon} />
            <h4 className={compact ? classes.voiceCloseConfirmTitleCompact : classes.voiceCloseConfirmTitle}>
              {translate('Voice Session Active')}
            </h4>
          </div>
          <p className={compact ? classes.voiceCloseConfirmTextCompact : classes.voiceCloseConfirmText}>
            {compact
              ? translate('What would you like to do?')
              : translate('Ada is currently listening. What would you like to do?')}
          </p>
          <div className={compact ? classes.voiceCloseConfirmActionsCompact : classes.voiceCloseConfirmActions}>
            <button
              className={`${compact ? classes.voiceCloseConfirmButtonCompact : classes.voiceCloseConfirmButton} ${
                classes.voiceCloseConfirmButtonSecondary
              }`}
              onClick={onKeepListening}
            >
              {compact ? translate('Keep Listening') : translate('Keep Listening in Background')}
            </button>
            <button
              className={`${compact ? classes.voiceCloseConfirmButtonCompact : classes.voiceCloseConfirmButton} ${
                classes.voiceCloseConfirmButtonPrimary
              }`}
              onClick={onEndSession}
            >
              {compact ? translate('End & Close') : translate('End Voice Session & Close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VoiceCloseConfirmBanner
